import React, { useReducer, useContext, useEffect, useState } from "react";
import { Collapse } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { MenuList } from "./Menu";
import { useSelector, useDispatch } from "react-redux";
import {
  selectUser,
  selectUserModules,
  selectUserRole,
  selectUserPagePermissions,
  logout,
} from "../../../store/authSlice";
import {
  appApi,
  useGetChatUnreadCountQuery,
  useGetRealEstateTeamsQuery,
} from "../../../store/appApi";
import { selectActiveProjectId } from "../../../store/projectSlice";

import { useScrollPosition } from "@n8tb1t/use-scroll-position";
import { ThemeContext } from "../../../context/ThemeContext";
import { closeMobileMenu } from "./NavHader";
import ChangePassword from "./ChangePassword";

const LEVELS = { none: 0, read: 1, "read-write": 2 };

// Resolve the effective access level for a page key.
//
// A page permission of "inherit" (the default every page ships with — see
// DEFAULT_PAGE_PERMISSIONS in AddStaff/EditStaff) or an unset/blank value means
// "fall back to the parent module's level". The module is the first dotted
// segment of the pageKey (e.g. "workforce.leave.apply" → "workforce"), with the
// menu group's own `module` prop considered too. Without this, a staff member
// granted the whole "workforce" module still saw no Leave / Attendance links,
// because those pages sat at the literal string "inherit" which scored 0.
// This mirrors hasPageAccess() in the mobile app's authSlice.
const pageAccessLevel = (pageKey, userModules = {}, userPagePerms = {}, groupModule = null) => {
  const stored = userPagePerms[pageKey];
  if (stored && stored !== "inherit") return LEVELS[stored] ?? 0;
  const derivedModule = pageKey ? pageKey.split(".")[0] : null;
  return Math.max(
    LEVELS[userModules[groupModule] ?? "none"] ?? 0,
    LEVELS[userModules[derivedModule] ?? "none"] ?? 0,
  );
};

const reducer = (previousState, updatedState) => ({
  ...previousState,
  ...updatedState,
});

const initialState = {
  active: "",
  activeSubmenu: "",
};

// ── RBAC helpers ──────────────────────────────────────────────────────────────
// `reTeamHead` items are also shown to whoever heads a Real Estate team,
// whatever their role.
const isVisible = (item, userRole, isRETeamHead = false, userId = "") => {
  if (item.userIds && !item.userIds.includes(String(userId))) return false;
  if (item.roles && !item.roles.includes(userRole) && !(item.reTeamHead && isRETeamHead))
    return false;
  if (item.hideForRoles && item.hideForRoles.includes(userRole)) return false;
  return true;
};

const getTitle = (item, userRole) =>
  (item.titleByRole && item.titleByRole[userRole]) || item.title;

// Collect all pageKeys from a menu group (flattens nested content arrays)
const collectPageKeys = (items = []) => {
  const keys = [];
  items.forEach((item) => {
    if (item.pageKey) keys.push(item.pageKey);
    if (item.content) keys.push(...collectPageKeys(item.content));
  });
  return keys;
};

// Modules declared on any nested submenu (e.g. "Customer Status Report").
const collectNestedModules = (items = []) => {
  const mods = [];
  items.forEach((item) => {
    if (item.module) mods.push(item.module);
    if (item.content) mods.push(...collectNestedModules(item.content));
  });
  return mods;
};

const isModuleVisible = (group, userRole, userModules, userPagePerms = {}) => {
  if (!group.module) return true;
  if (userRole === "super-admin") return true;
  // Check module-level access first
  if ((LEVELS[userModules[group.module] ?? "none"] ?? 0) >= LEVELS["read"])
    return true;
  // If module is "none", check whether any page in this group is reachable
  // (explicit grant, or "inherit" resolving up to a granted module).
  const keys = collectPageKeys(group.content ?? []);
  if (
    keys.some(
      (k) =>
        pageAccessLevel(k, userModules, userPagePerms, group.module) >=
        LEVELS["read"],
    )
  )
    return true;
  // …or a nested submenu declares a module the user does have.
  return collectNestedModules(group.content ?? []).some(
    (m) => (LEVELS[userModules[m] ?? "none"] ?? 0) >= LEVELS["read"],
  );
};

// Returns true if a leaf item should be visible, considering module + page permissions.
// groupModule may be null when called from deeply nested render (variable shadowing).
// Items without a pageKey are shown when parent group is already visible (parent already gated).
const isPageVisible = (
  item,
  userRole,
  userModules,
  groupModule,
  userPagePerms = {},
) => {
  if (userRole === "super-admin") return true;
  // Opts an item out of module/page gating entirely — every authenticated
  // user sees it (e.g. internal chat, open to the whole company).
  if (item.public) return true;
  // An item (or nested submenu) that declares its own `module` is gated by
  // THAT module only — it overrides whatever the parent group's module was.
  if (item.module) {
    return (LEVELS[userModules[item.module] ?? "none"] ?? 0) >= LEVELS["read"];
  }
  if (
    groupModule &&
    (LEVELS[userModules[groupModule] ?? "none"] ?? 0) >= LEVELS["read"]
  )
    return true;
  if (item.pageKey)
    return (
      pageAccessLevel(item.pageKey, userModules, userPagePerms, groupModule) >=
      LEVELS["read"]
    );
  // No pageKey and no module context — hide by default (shouldn't happen for properly annotated items)
  return false;
};

// ── Compact "select a project" modal ─────────────────────────────────────────
const ProjectRequiredModal = ({ onClose, onGoToDashboard }) => (
  <>
    {/* Backdrop */}
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.35)",
        zIndex: 9998,
      }}
    />
    {/* Modal */}
    <div
      style={{
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        zIndex: 9999,
        background: "#fff",
        borderRadius: 12,
        boxShadow: "0 8px 40px rgba(0,0,0,0.18)",
        padding: "32px 28px 24px",
        width: 340,
        maxWidth: "90vw",
        textAlign: "center",
      }}
    >
      <div
        style={{
          width: 52,
          height: 52,
          borderRadius: "50%",
          background: "#3243361a",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "0 auto 16px",
        }}
      >
        <i
          className="fas fa-building"
          style={{ color: "#324336", fontSize: 22 }}
        />
      </div>
      <h5 style={{ fontWeight: 700, marginBottom: 8, color: "#1a1a2e" }}>
        No Project Selected
      </h5>
      <p
        style={{
          color: "#6c757d",
          fontSize: 14,
          marginBottom: 24,
          lineHeight: 1.5,
        }}
      >
        Please select a project to proceed. All module navigation is tied to an
        active project context.
      </p>
      <button
        className="btn btn-primary w-100"
        style={{ borderRadius: 8, fontWeight: 600 }}
        onClick={onGoToDashboard}
      >
        Select a Project
      </button>
      <button
        className="btn btn-link w-100 mt-2"
        style={{ fontSize: 13, color: "#aaa" }}
        onClick={onClose}
      >
        Dismiss
      </button>
    </div>
  </>
);

const SideBar = () => {
  let dat = new Date();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    iconHover,
    sidebarposition,
    headerposition,
    sidebarLayout,
    ChangeIconSidebar,
    windowWidth,
  } = useContext(ThemeContext);

  // On mobile the sidebar is an overlay covering the page — close it once a
  // destination is picked so it doesn't stay open on top of the new page.
  const handleNavClick = () => {
    if (windowWidth <= 768) closeMobileMenu();
  };

  // ── Auth ─────────────────────────────────────────────────────────────────
  const currentUser = useSelector(selectUser);
  const userRole = useSelector(selectUserRole);
  const userModules = useSelector(selectUserModules);
  const userPagePerms = useSelector(selectUserPagePermissions);
  const activeProjectId = useSelector(selectActiveProjectId);
  // Lists only the teams this user heads (non-admins), so non-empty = team head.
  const { data: reTeamData } = useGetRealEstateTeamsQuery(
    {},
    { skip: !userRole || userRole === "super-admin" },
  );
  const isRETeamHead = (reTeamData?.teams || []).length > 0;

  // Unread chat badge on the "Messages" nav item — polled since a message
  // can arrive while the user is anywhere else in the app, not just on the
  // Messages page where the socket connection actually lives.
  const { data: chatUnread } = useGetChatUnreadCountQuery(undefined, {
    pollingInterval: 30000,
  });
  const unreadChatCount = chatUnread?.count || 0;

  // Sidebar is locked whenever there is no active project context
  const isLocked = !activeProjectId;

  const [showModal, setShowModal] = useState(false);
  const [state, setState] = useReducer(reducer, initialState);

  const [hideOnScroll, setHideOnScroll] = useState(true);
  useScrollPosition(
    ({ prevPos, currPos }) => {
      const isShow = currPos.y > prevPos.y;
      if (isShow !== hideOnScroll) setHideOnScroll(isShow);
    },
    [hideOnScroll],
  );

  // Auto-dismiss modal when a project gets selected
  useEffect(() => {
    if (activeProjectId && showModal) setShowModal(false);
  }, [activeProjectId, showModal]);

  const handleMenuActive = (status) => {
    setState({ active: status });
    if (state.active === status) setState({ active: "" });
  };
  const handleSubmenuActive = (status) => {
    setState({ activeSubmenu: status });
    if (state.activeSubmenu === status) setState({ activeSubmenu: "" });
  };

  const fullPath = window.location.pathname;
  let path = fullPath.split("/");
  path = path[path.length - 1];

  const isActive = (to) => {
    if (!to) return false;
    return to.startsWith("/") ? fullPath === to : path === to;
  };

  useEffect(() => {
    MenuList.forEach((data) => {
      data.content?.forEach((item) => {
        if (isActive(item.to)) setState({ active: data.title });
        item.content?.forEach((ele) => {
          if (isActive(ele.to))
            setState({ activeSubmenu: item.title, active: data.title });
        });
      });
    });
  }, [path, fullPath]);

  const handleLockedClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowModal(true);
  };

  const handleLogout = () => {
    // Reset all RTK Query cache so no previous user's data bleeds into next session
    dispatch(appApi.util.resetApiState());
    // Clear Redux auth state + localStorage + axios Authorization header
    dispatch(logout());
    navigate("/login");
  };

  return (
    <>
      <div
        onMouseEnter={() => ChangeIconSidebar(true)}
        onMouseLeave={() => ChangeIconSidebar(false)}
        className={`dlabnav ${iconHover} ${
          sidebarposition.value === "fixed" &&
          sidebarLayout.value === "horizontal" &&
          headerposition.value === "static"
            ? hideOnScroll > 120
              ? "fixed"
              : ""
            : ""
        }`}
      >
        {/*
          Inner wrapper acts as the positioning context for the lockout overlay
          so we don't disturb the .dlabnav element's own CSS positioning.
        */}
        <div style={{ position: "relative", height: "100%" }}>
          {/* ── Locked overlay — intercepts all clicks when no project is active ── */}
          {isLocked && (
            <div
              onClick={handleLockedClick}
              title="Select a project to enable navigation"
              style={{
                position: "absolute",
                inset: 0,
                zIndex: 100,
                cursor: "not-allowed",
                background: "transparent",
              }}
            />
          )}

          <div
            className="dlabnav-scroll"
            style={
              isLocked
                ? { opacity: 0.38, pointerEvents: "none", userSelect: "none" }
                : undefined
            }
          >
            <ul className="metismenu" id="menu">
              {MenuList.map((data, index) => {
                let menuClass = data.classsChange;

                // `roles`/`hideForRoles` on a top-level group (e.g. Reports)
                // was only ever wired up for nested content items — add it
                // here too so a group-level role gate actually takes effect.
                if (!isVisible(data, userRole, isRETeamHead, currentUser?.localId))
                  return null;

                if (
                  !isModuleVisible(data, userRole, userModules, userPagePerms)
                )
                  return null;

                // Groups pinned to specific projects (e.g. Netplus Modem
                // Status → Satti & Co only) hide unless that project is active.
                if (
                  Array.isArray(data.projectOnly) &&
                  !data.projectOnly.includes(activeProjectId)
                )
                  return null;

                const _groupModule = data.module; // capture before shadowing
                const visibleContent = data.content
                  ? data.content
                      .filter((item) =>
                        isVisible(item, userRole, isRETeamHead, currentUser?.localId),
                      )
                      .filter((item) => {
                        // A nested submenu can declare its own `module`; when it
                        // does, that module gates the whole submenu and its
                        // children (see isPageVisible).
                        const childGroupModule = item.module || _groupModule;
                        // For submenus with nested content, check if any child is visible
                        if (item.content) {
                          if (
                            userRole !== "super-admin" &&
                            item.module &&
                            (LEVELS[userModules[item.module] ?? "none"] ?? 0) <
                              LEVELS["read"]
                          )
                            return false;
                          const visibleChildren = item.content.filter((child) =>
                            isPageVisible(
                              child,
                              userRole,
                              userModules,
                              childGroupModule,
                              userPagePerms,
                            ),
                          );
                          return visibleChildren.length > 0;
                        }
                        return isPageVisible(
                          item,
                          userRole,
                          userModules,
                          childGroupModule,
                          userPagePerms,
                        );
                      })
                      .map((item) => ({
                        ...item,
                        _parentModule: item.module || _groupModule,
                      }))
                  : data.content;

                if (
                  data.content &&
                  data.content.length > 0 &&
                  visibleContent.length === 0
                ) {
                  return null;
                }

                if (menuClass === "menu-title") {
                  return (
                    <li
                      className={`nav-label ${menuClass} ${data.extraclass}`}
                      key={index}
                    >
                      {data.title}
                    </li>
                  );
                } else {
                  return (
                    <li
                      className={`${state.active === data.title ? "mm-active" : ""}${isActive(data.to) ? "mm-active" : ""}`}
                      key={index}
                    >
                      {visibleContent && visibleContent.length > 0 ? (
                        <>
                          <Link
                            to={"#"}
                            className="has-arrow"
                            onClick={() => handleMenuActive(data.title)}
                          >
                            {data.iconStyle}
                            <span className="nav-text">{data.title}</span>
                            {data.title === "Messages" && unreadChatCount > 0 ? (
                              <span className="badge badge-xs style-1 badge-danger">
                                {unreadChatCount > 99 ? "99+" : unreadChatCount}
                              </span>
                            ) : (
                              <span className="badge badge-xs style-1 badge-danger">
                                {data.update}
                              </span>
                            )}
                          </Link>
                          <Collapse in={state.active === data.title}>
                            <ul
                              className={`${menuClass === "mm-collapse" ? "mm-show" : ""}`}
                            >
                              {visibleContent.map((data, index) => (
                                <li
                                  key={index}
                                  className={`${state.activeSubmenu === data.title ? "mm-active" : ""}${isActive(data.to) ? "mm-active" : ""}`}
                                >
                                  {data.content && data.content.length > 0 ? (
                                    <>
                                      <Link
                                        to={data.to}
                                        className={
                                          data.hasMenu ? "has-arrow" : ""
                                        }
                                        onClick={() =>
                                          handleSubmenuActive(data.title)
                                        }
                                      >
                                        {getTitle(data, userRole)}
                                      </Link>
                                      <Collapse
                                        in={state.activeSubmenu === data.title}
                                      >
                                        <ul
                                          className={`${menuClass === "mm-collapse" ? "mm-show" : ""}`}
                                        >
                                          {data.content
                                            .filter((child) =>
                                              isVisible(child, userRole, isRETeamHead),
                                            )
                                            .filter((child) =>
                                              isPageVisible(
                                                child,
                                                userRole,
                                                userModules,
                                                data._parentModule ?? null,
                                                userPagePerms,
                                              ),
                                            )
                                            .map((data, index) => (
                                              <li key={index}>
                                                <Link
                                                  className={`${isActive(data.to) ? "mm-active" : ""}`}
                                                  to={data.to}
                                                  onClick={handleNavClick}
                                                >
                                                  {getTitle(data, userRole)}
                                                </Link>
                                              </li>
                                            ))}
                                        </ul>
                                      </Collapse>
                                    </>
                                  ) : (
                                    <Link
                                      to={data.to}
                                      className={`${data.to === path ? "mm-active" : ""}`}
                                      onClick={handleNavClick}
                                    >
                                      {getTitle(data, userRole)}
                                    </Link>
                                  )}
                                </li>
                              ))}
                            </ul>
                          </Collapse>
                        </>
                      ) : (
                        <Link
                          to={data.to}
                          className={`${data.to === path ? "mm-active" : ""}`}
                          onClick={handleNavClick}
                        >
                          {data.iconStyle}
                          <span className="nav-text">{data.title}</span>
                        </Link>
                      )}
                    </li>
                  );
                }
              })}
              <li>
                <ChangePassword
                  renderTrigger={(open) => (
                    <Link
                      to="#"
                      onClick={(e) => {
                        e.preventDefault();
                        closeMobileMenu();
                        open();
                      }}
                    >
                      <i
                        className="fa-solid fa-key"
                        style={{ color: "#6366F1" }}
                      />
                      <span className="nav-text">Change Password</span>
                    </Link>
                  )}
                />
              </li>
              <li>
                <Link
                  to="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleLogout();
                  }}
                >
                  <i
                    className="fa-solid fa-right-from-bracket"
                    style={{ color: "#EF4444" }}
                  />
                  <span className="nav-text">Logout</span>
                </Link>
              </li>
            </ul>
            <div className="copyright">
              <p>Satti &Co. Admin © {dat.getFullYear()} All Rights Reserved</p>
            </div>
          </div>
        </div>
        {/* end inner positioning wrapper */}
      </div>

      {/* ── Project-required modal — rendered in a portal above everything ── */}
      {showModal && (
        <ProjectRequiredModal
          onClose={() => setShowModal(false)}
          onGoToDashboard={() => {
            setShowModal(false);
            navigate("/dashboard");
          }}
        />
      )}
    </>
  );
};

export default SideBar;
